import { InstallmentStatus } from '../enums/SystemEnums';

/** Presentation-agnostic status tone (compatible with UI BadgeTone). */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export type InstallmentDisplayStatus = InstallmentStatus | 'NOT_GENERATED' | 'UNKNOWN';

export interface InstallmentStatusSpec {
  status: InstallmentDisplayStatus;
  labelKey: string;
  defaultLabel: string;
  tone: StatusTone;
  glyph: string;
  summaryGroup: 'Settled' | 'Payable' | 'Future' | 'None';
  isPayable: boolean;
  countsAsCleared: boolean;
  isPrepaid: boolean;
}

/**
 * Solidified Status Table per DEVELOPMENT_PLAN.md §6.3 (Q2, BD-19, §3.2).
 * Single shared source of truth for grid, ledger, modal, and member-360.
 */
export const INSTALLMENT_STATUS_TABLE: Record<InstallmentDisplayStatus, InstallmentStatusSpec> = {
  [InstallmentStatus.PAID]: {
    status: InstallmentStatus.PAID,
    labelKey: 'installments.paid',
    defaultLabel: 'Paid',
    tone: 'success',
    glyph: '✓',
    summaryGroup: 'Settled',
    isPayable: false,
    countsAsCleared: true,
    isPrepaid: false,
  },
  [InstallmentStatus.ADVANCED]: {
    status: InstallmentStatus.ADVANCED,
    labelKey: 'installments.advanced',
    defaultLabel: 'Prepaid',
    tone: 'info',
    glyph: '⟳',
    summaryGroup: 'Settled',
    isPayable: false,
    countsAsCleared: true,
    isPrepaid: true,
  },
  [InstallmentStatus.DUE]: {
    status: InstallmentStatus.DUE,
    labelKey: 'installments.due',
    defaultLabel: 'Due',
    tone: 'warning',
    glyph: '!',
    summaryGroup: 'Payable',
    isPayable: true,
    countsAsCleared: false,
    isPrepaid: false,
  },
  [InstallmentStatus.IN_NOTICE_PERIOD]: {
    status: InstallmentStatus.IN_NOTICE_PERIOD,
    labelKey: 'installments.in_notice_period',
    defaultLabel: 'In Notice Period',
    tone: 'warning',
    glyph: '!',
    summaryGroup: 'Payable',
    isPayable: true,
    countsAsCleared: false,
    isPrepaid: false,
  },
  [InstallmentStatus.DEFAULTED]: {
    status: InstallmentStatus.DEFAULTED,
    labelKey: 'installments.defaulted',
    defaultLabel: 'Defaulted',
    tone: 'danger',
    glyph: '⚠',
    summaryGroup: 'Payable',
    isPayable: true,
    countsAsCleared: false,
    isPrepaid: false,
  },
  [InstallmentStatus.UPCOMING]: {
    status: InstallmentStatus.UPCOMING,
    labelKey: 'installments.upcoming',
    defaultLabel: 'Upcoming',
    tone: 'neutral',
    glyph: '—',
    summaryGroup: 'Future',
    isPayable: false,
    countsAsCleared: false,
    isPrepaid: false,
  },
  NOT_GENERATED: {
    status: 'NOT_GENERATED',
    labelKey: 'installments.not_generated',
    defaultLabel: 'Not Generated',
    tone: 'neutral',
    glyph: '·',
    summaryGroup: 'None',
    isPayable: false,
    countsAsCleared: false,
    isPrepaid: false,
  },
  UNKNOWN: {
    status: 'UNKNOWN',
    labelKey: 'installments.status_unavailable',
    defaultLabel: 'Status Unavailable',
    tone: 'neutral',
    glyph: '·',
    summaryGroup: 'None',
    isPayable: false,
    countsAsCleared: false,
    isPrepaid: false,
  },
};

/**
 * Returns 'YYYY-MM-DD' representing the current calendar day in Asia/Dhaka.
 */
export function getDhakaTodayDateString(refDate: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Dhaka',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(refDate);
  } catch {
    const d = new Date(refDate.getTime() + 6 * 3600 * 1000);
    return d.toISOString().slice(0, 10);
  }
}

/**
 * Evaluates whether an installment due date is on or before today in Asia/Dhaka.
 */
export function isDueDatePastOrTodayDhaka(
  dueDate?: string | null,
  refDate: Date = new Date(),
): boolean {
  if (!dueDate || typeof dueDate !== 'string') return false;
  const dayStr = dueDate.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayStr)) return false;
  return dayStr <= getDhakaTodayDateString(refDate);
}

/**
 * Resolves effective status with the interim safety net per §6.3 Q1-B:
 * Render `UPCOMING && dueDate <= today (Asia/Dhaka)` as DUE.
 * Client never otherwise re-classifies from clock.
 */
export function resolveEffectiveInstallmentStatus(
  status: InstallmentStatus | string | null | undefined,
  dueDate?: string | null,
  refDate: Date = new Date(),
): InstallmentDisplayStatus {
  if (!status) return 'NOT_GENERATED';
  if (status === InstallmentStatus.UPCOMING && isDueDatePastOrTodayDhaka(dueDate, refDate)) {
    return InstallmentStatus.DUE;
  }
  if (Object.values(InstallmentStatus).includes(status as InstallmentStatus)) {
    return status as InstallmentStatus;
  }
  return 'UNKNOWN';
}

/**
 * Get the full status specification for an installment row.
 */
export function getInstallmentStatusSpec(
  status: InstallmentStatus | string | null | undefined,
  dueDate?: string | null,
  refDate: Date = new Date(),
): InstallmentStatusSpec {
  const resolved = resolveEffectiveInstallmentStatus(status, dueDate, refDate);
  return INSTALLMENT_STATUS_TABLE[resolved];
}

/**
 * True only for payable rows (DUE, IN_NOTICE_PERIOD, DEFAULTED, and interim safety net).
 * UPCOMING and ADVANCED are strictly NOT payable directly.
 */
export function isInstallmentPayable(
  status: InstallmentStatus | string | null | undefined,
  dueDate?: string | null,
  refDate: Date = new Date(),
): boolean {
  return getInstallmentStatusSpec(status, dueDate, refDate).isPayable;
}

/**
 * True if status counts toward cleared progress (PAID or ADVANCED).
 */
export function isInstallmentCleared(
  status: InstallmentStatus | string | null | undefined,
): boolean {
  if (!status) return false;
  return status === InstallmentStatus.PAID || status === InstallmentStatus.ADVANCED;
}
